import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, Award, Users, Bell, Sparkles, ArrowRight, Sunrise, Sun, Sunset, Moon } from "lucide-react";
import CompartirCatalogoCard from "@/components/CompartirCatalogoCard";
import { useNavigate } from "react-router-dom";

/**
 * "Mi Día" — vista personalizada para vendedoras.
 *
 * Muestra en un solo scroll:
 *  - saludo dinámico según hora del día
 *  - Pedidos del día (creados y aún no confirmados)
 *  - Comisión estimada del mes en curso + progreso vs mes anterior
 *  - Clientes que le corresponden y no han comprado hace 14+ días
 *  - Notificaciones críticas sin leer
 *  - Frase de motivación
 */
function saludoActual() {
  const h = new Date().getHours();
  if (h < 6) return { text: "Buenas madrugadas", icon: Moon };
  if (h < 12) return { text: "Buenos días", icon: Sunrise };
  if (h < 18) return { text: "Buenas tardes", icon: Sun };
  return { text: "Buenas noches", icon: Sunset };
}

const FRASES = [
  "Cada llamada es una oportunidad. Un mensaje puede ser una venta.",
  "Los grandes números empiezan con una remisión bien cargada.",
  "Un cliente contento vuelve. Un cliente muy contento trae a otro.",
  "Tu esfuerzo esta poniendo a Beem en el mapa.",
  "El proximo pedido esta a un correo de distancia.",
];

export default function MiDiaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({
    kpis: { ventas_mes: 0, comision_mes: 0, pedidos_hoy: 0, pedidos_mes: 0, clientes_dormidos_count: 0, alertas_criticas: 0, delta_pct: null },
    pedidos_hoy: [],
    top_clientes: [],
    top_productos: [],
    clientes_dormidos: [],
    frase: FRASES[Math.floor(Math.random() * FRASES.length)],
  });
  const [loading, setLoading] = useState(true);
  const [ordenTop, setOrdenTop] = useState("valor");
  const saludo = saludoActual();
  const nombre = (user?.name || user?.email || "").split(" ")[0];

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ordenTop]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reportes/mi-dia?orden_top=${ordenTop}`);
      setData((d) => ({ ...d, ...data }));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative p-8 md:p-10 rounded-3xl bg-gradient-to-br from-primary/12 via-accent to-nude/50 border border-primary/15 overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 opacity-10">
          <img src="/beem-logo.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary/90 font-semibold">
            <saludo.icon className="w-3.5 h-3.5" />
            <span>{saludo.text}</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 text-foreground" style={{ fontStyle: "italic" }}>
            {saludo.text}, {nombre}.
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{data.frase}</p>
        </div>
      </div>

      {/* Compartir catálogo con clientes */}
      <CompartirCatalogoCard />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniCard title="Ventas del mes" value={formatCOP(data.kpis.ventas_mes)} icon={ShoppingCart}
          sub={data.kpis.delta_pct !== null ? `${data.kpis.delta_pct > 0 ? "↑" : "↓"} ${Math.abs(data.kpis.delta_pct)}% vs mes anterior` : "Primer mes con ventas"}
          highlight testid="mid-ventas-mes" />
        <MiniCard title="Comisión del mes" value={formatCOP(data.kpis.comision_mes)} icon={Award} sub={`${data.kpis.pedidos_mes} pedido${data.kpis.pedidos_mes !== 1 ? "s" : ""} este mes`} onClick={() => navigate("/reportes")} testid="mid-comision" />
        <MiniCard title="Clientes por retomar" value={data.kpis.clientes_dormidos_count} icon={Users} sub="+14 días sin comprar" onClick={() => navigate("/clientes")} testid="mid-dormidos" />
        <MiniCard title="Alertas críticas" value={data.kpis.alertas_criticas} icon={Bell} onClick={() => navigate("/notificaciones")} testid="mid-alertas" />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top clientes del mes */}
        <div className="p-6 rounded-3xl bg-card border border-border/70">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Ranking del mes</div>
            <h3 className="font-display text-2xl mt-1 text-foreground" style={{ fontStyle: "italic" }}>Sus mejores clientes</h3>
          </div>
          {data.top_clientes.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aún sin ventas este mes. La primera está a la vuelta de la esquina.
            </div>
          ) : (
            <div className="space-y-2">
              {data.top_clientes.map((c, i) => (
                <div key={c.cliente_id} className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-accent/40" data-testid={`mid-top-cli-${i}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs ${i === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{c.cliente_nombre}</div>
                    <div className="text-[10px] text-muted-foreground">{c.pedidos} pedido{c.pedidos !== 1 ? "s" : ""}</div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums">{formatCOP(c.total)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top productos + clientes dormidos side by side */}
        <div className="space-y-6">
          {data.top_productos.length > 0 && (
            <div className="p-6 rounded-3xl bg-primary/5 border border-primary/20">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Sus productos estrella</div>
                  <h3 className="font-display text-xl mt-1 text-foreground" style={{ fontStyle: "italic" }}>Lo que más vendió</h3>
                </div>
                <div className="flex bg-card border border-border rounded-full p-0.5" title="Cambia el criterio de ordenamiento">
                  <button
                    onClick={() => setOrdenTop("valor")}
                    data-testid="midia-orden-valor"
                    className={`px-2.5 h-7 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${ordenTop === "valor" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    title="Ordenar por dinero vendido"
                  >
                    $ Valor
                  </button>
                  <button
                    onClick={() => setOrdenTop("unidades")}
                    data-testid="midia-orden-unidades"
                    className={`px-2.5 h-7 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${ordenTop === "unidades" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    title="Ordenar por unidades vendidas"
                  >
                    # Unidades
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {data.top_productos.slice(0, 4).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="truncate flex-1 min-w-0">
                      <span className="text-foreground font-medium">{p.producto_nombre}</span>
                      <span className="text-muted-foreground text-xs">
                        {" · "}
                        {ordenTop === "unidades" ? formatCOP(p.subtotal) : `${formatNumber(p.cantidad)} unid`}
                      </span>
                    </div>
                    <span className="font-semibold tabular-nums">
                      {ordenTop === "unidades" ? `${formatNumber(p.cantidad)} unid` : formatCOP(p.subtotal)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clientes dormidos */}
          <div className="p-6 rounded-3xl bg-card border border-border/70">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Retomar contacto</div>
                <h3 className="font-display text-xl mt-1 text-foreground" style={{ fontStyle: "italic" }}>Clientes que hace tiempo no compran</h3>
              </div>
              <button onClick={() => navigate("/clientes")} className="text-xs text-primary hover:underline flex items-center gap-1">
                Ver todos <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {data.clientes_dormidos.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Todos sus clientes están al día. 🌟
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.clientes_dormidos.slice(0, 4).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-accent/50 cursor-pointer group" onClick={() => navigate("/clientes")} data-testid={`mid-cli-${c.id}`}>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 text-primary flex items-center justify-center font-semibold text-[11px] font-display italic">
                      {(c.nombre || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">{c.nombre}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {c.zona || "Sin zona"} · {c.ultimo_pedido ? `Última: ${c.ultimo_pedido.slice(0, 10)}` : "Sin compras"}
                      </div>
                    </div>
                    <ArrowRight className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pedidos hoy */}
      <div className="p-6 rounded-3xl bg-card border border-border/70">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Actividad de hoy</div>
            <h3 className="font-display text-2xl mt-1 text-foreground" style={{ fontStyle: "italic" }}>Pedidos que armaste hoy</h3>
          </div>
          <button onClick={() => navigate("/pedidos")} className="text-xs text-primary hover:underline flex items-center gap-1">
            Ver todos los pedidos <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {data.pedidos_hoy.length === 0 ? (
          <div className="py-8 text-center">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-primary/50" />
            <div className="text-sm text-muted-foreground">Aún no armaste ningún pedido hoy.</div>
            <button onClick={() => navigate("/pedidos")} className="mt-3 text-xs bg-primary text-primary-foreground px-4 py-2 rounded-full font-semibold hover:bg-primary/90 btn-tactile" data-testid="mid-nuevo-pedido">
              Armar el primero
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.pedidos_hoy.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => navigate("/pedidos")}>
                <div className={`w-1 h-8 rounded-full ${p.estado === "confirmado" ? "bg-blue-500" : p.estado === "entregado" ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{p.cliente_nombre}</div>
                  <div className="text-[11px] text-muted-foreground truncate">#{p.numero} · {p.items?.length || 0} productos · {p.estado}</div>
                </div>
                <div className="text-sm font-semibold text-foreground tabular-nums">{formatCOP(p.total || 0)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alertas críticas — solo si hay */}
      {data.kpis.alertas_criticas > 0 && (
        <div className="p-6 rounded-3xl bg-amber-500/5 border-l-4 border-l-amber-500 cursor-pointer" onClick={() => navigate("/notificaciones")}>
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-foreground">
              Tienes {data.kpis.alertas_criticas} alerta{data.kpis.alertas_criticas !== 1 ? "s" : ""} crítica{data.kpis.alertas_criticas !== 1 ? "s" : ""} sin leer
            </h3>
            <ArrowRight className="w-4 h-4 text-amber-600 ml-auto" />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniCard({ title, value, icon: Icon, onClick, highlight, testid }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 md:p-5 rounded-3xl border text-left transition-all hover:-translate-y-[2px] btn-tactile overflow-hidden ${
        highlight ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/30"
      }`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold min-w-0 flex-1 leading-tight ${highlight ? "opacity-70" : "text-muted-foreground"}`}>{title}</div>
        <Icon className={`w-4 h-4 flex-shrink-0 ${highlight ? "opacity-90" : "text-muted-foreground/60"}`} strokeWidth={1.8} />
      </div>
      <div
        className={`text-lg md:text-3xl font-semibold mt-2 md:mt-3 tabular-nums ${highlight ? "" : "text-foreground"}`}
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
        title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
      >
        {value}
      </div>
    </button>
  );
}

